// v7.05 — shared theme for the four auth pages (login, signup,
// forgot-password, reset-password). Tokens were lifted verbatim from
// admoji.com/ads's computed styles: warm cream page, near-black ink, white
// cards with a hairline border and NO shadow, black buttons, Inter Tight
// with an Instrument Serif italic accent line and a JetBrains Mono
// micro-label. PINK is the marketing accent — 6.3:1 on white.
//
// Single source of truth on purpose: these pages are one flow and drifted
// three different ways before (dark login, indigo signup/forgot/reset).
import type { CSSProperties, FocusEvent } from "react";

export const PAGE = "#F2F1EE";
export const CARD = "#FFFFFF";
export const INK = "#161616";
export const MUTED = "#5E5E5A";
export const LINE = "rgba(17,17,17,0.16)";
export const FIELD = "#FAF9F7";
export const BTN = "#141414";
export const BTN_HOVER = "#2E2E2E";
export const PINK = "#7b1f40";

export const SANS = '"Inter Tight", -apple-system, "system-ui", "Segoe UI", sans-serif';
export const SERIF = '"Instrument Serif", Georgia, serif';
export const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

export const pageStyle: CSSProperties = { backgroundColor: PAGE, fontFamily: SANS };

export const cardStyle: CSSProperties = {
  backgroundColor: CARD,
  border: `1px solid ${LINE}`,
  borderRadius: 14,
  padding: "34px 32px 30px",
};

/** Tiny uppercase mono kicker above the heading. */
export const monoLabelStyle: CSSProperties = {
  fontFamily: MONO,
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: MUTED,
  marginBottom: 10,
};

export const h1Style: CSSProperties = {
  fontFamily: SANS,
  fontSize: 34,
  fontWeight: 600,
  letterSpacing: "-0.032em",
  lineHeight: 1.08,
  color: INK,
  margin: 0,
};

/** The italic serif half of a heading — the site's signature move. */
export const serifStyle: CSSProperties = {
  fontFamily: SERIF,
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: 37,
};

export const subStyle: CSSProperties = { fontSize: 13.5, color: MUTED, marginTop: 8 };

export const labelStyle: CSSProperties = { fontSize: 12.5, fontWeight: 500, color: MUTED };

export const fieldStyle: CSSProperties = {
  backgroundColor: FIELD,
  border: `1px solid ${LINE}`,
  borderRadius: 9,
  color: INK,
  fontSize: 14,
  fontFamily: SANS,
};

export const buttonStyle: CSSProperties = {
  height: 46,
  backgroundColor: BTN,
  color: "#FFFFFF",
  border: "1px solid transparent",
  borderRadius: 11,
  fontSize: 14.5,
  fontWeight: 600,
  fontFamily: SANS,
};

export const errorStyle: CSSProperties = {
  backgroundColor: "rgba(123,31,64,0.07)",
  color: "#8F2748",
  border: "1px solid rgba(123,31,64,0.32)",
  borderRadius: 9,
};

export const footerStyle: CSSProperties = { fontSize: 11.5, color: MUTED };

export const fieldFocus = (e: FocusEvent<HTMLInputElement>) => {
  e.target.style.outline = "none";
  e.target.style.borderColor = PINK;
  e.target.style.boxShadow = "0 0 0 3px rgba(123,31,64,0.15)";
  e.target.style.backgroundColor = CARD;
};

export const fieldBlur = (e: FocusEvent<HTMLInputElement>) => {
  e.target.style.borderColor = LINE;
  e.target.style.boxShadow = "none";
  e.target.style.backgroundColor = FIELD;
};

/** Shared dark-button hover pair. */
export const btnHoverOn = (e: { currentTarget: HTMLButtonElement }, loading?: boolean) => {
  if (!loading) e.currentTarget.style.backgroundColor = BTN_HOVER;
};
export const btnHoverOff = (e: { currentTarget: HTMLButtonElement }) => {
  e.currentTarget.style.backgroundColor = BTN;
};
